/**
 * App chrome theme — Light / Dark / System, persisted as `mpp:theme:v1`
 * (prefs-synced). `system` follows the OS `prefers-color-scheme` and
 * updates live when the computer switches.
 */
import { safeLocalStorageGet, safeLocalStorageSet } from "@/lib/safeLocalStorage";

export const THEME_KEY = "mpp:theme:v1";
/** Resolved chrome the rest of the app paints — charts, tokens, `html.dark`. */
export type AppTheme = "light" | "dark";
/** What Settings stores. `system` is the default when nothing is saved. */
export type ThemePreference = AppTheme | "system";

export function isAppTheme(v: unknown): v is AppTheme {
  return v === "light" || v === "dark";
}

export function isThemePreference(v: unknown): v is ThemePreference {
  return v === "light" || v === "dark" || v === "system";
}

export function osPrefersDark(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

export function resolveTheme(pref: ThemePreference): AppTheme {
  if (pref === "system") return osPrefersDark() ? "dark" : "light";
  return pref;
}

export function readStoredPreference(): ThemePreference {
  const raw = safeLocalStorageGet(THEME_KEY);
  return isThemePreference(raw) ? raw : "system";
}

/** @deprecated resolved paint colour — use `readStoredPreference` + `resolveTheme`. */
export function readStoredTheme(): AppTheme {
  return resolveTheme(readStoredPreference());
}

export function applyThemeClass(theme: AppTheme): void {
  if (typeof document === "undefined") return;
  document.documentElement.classList.toggle("dark", theme === "dark");
}

export function persistThemePreference(pref: ThemePreference): void {
  safeLocalStorageSet(THEME_KEY, pref);
  applyThemeClass(resolveTheme(pref));
}

/** @deprecated use `persistThemePreference`. */
export function persistTheme(theme: AppTheme): void {
  persistThemePreference(theme);
}

/** Inline boot script — runs before paint to avoid a light flash on dark. */
export const THEME_BOOT_SCRIPT = `(function(){try{var t=localStorage.getItem(${JSON.stringify(THEME_KEY)});var sys=window.matchMedia("(prefers-color-scheme: dark)").matches;var dark=t==="dark"||((t==="system"||t==null||t==="")&&sys);if(dark)document.documentElement.classList.add("dark");else document.documentElement.classList.remove("dark");}catch(e){}})();`;
